import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, UserRoundX } from 'lucide-react';
import type { CustomerAccountDto, CustomerListResponse } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import {
  assignCard,
  customerSearchPath,
  fieldErrorFor,
  unassignCard,
  type PendingAssignCard,
} from './cards';

interface AssignCardPanelProps {
  /**
   * UID handed over by an unrecognised tap; prefills the card field. The
   * nonce is distinct per tap, so tapping the SAME card again re-prefills
   * even after staff cleared the field.
   */
  pendingCard: PendingAssignCard | null;
  /** A card was linked or removed — the parent clears its tap result. */
  onChanged: (account: CustomerAccountDto) => void;
}

/**
 * Link a card to an account, or remove one — SPEC §5.5 card management.
 *
 * The search reuses the customers list endpoint, which already matches name,
 * phone and card. Assignment PATCHes `cardId`; uniqueness is the server's
 * call (`card_taken` arrives as a field error and is shown at the input).
 * Unassign PATCHes an explicit null behind a confirm, because a removed card
 * silently kills that customer's tap-to-login.
 */
export function AssignCardPanel({ pendingCard, onChanged }: AssignCardPanelProps) {
  const { t } = useTranslation();

  const [cardIdDraft, setCardIdDraft] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const { rows, error: searchError, loading, replaceRow } = useCustomerSearch(searchDraft);

  const [cardFieldError, setCardFieldError] = useState<string | null>(null);
  const [rootError, setRootError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<CustomerAccountDto | null>(null);

  // Keyed on the NONCE, not the card id: a repeat tap of the same card bumps
  // the nonce and must prefill again (nextPendingAssign explains why).
  const pendingNonce = pendingCard?.nonce ?? null;
  const pendingCardId = pendingCard?.cardId ?? null;
  useEffect(() => {
    if (pendingNonce !== null && pendingCardId !== null) {
      setCardIdDraft(pendingCardId);
      setCardFieldError(null);
      setNotice(null);
    }
  }, [pendingNonce, pendingCardId]);

  async function handleAssign(customer: CustomerAccountDto): Promise<void> {
    setCardFieldError(null);
    setRootError(null);
    setNotice(null);
    setBusyId(customer.id);
    try {
      const account = await assignCard(customer.id, cardIdDraft);
      replaceRow(account);
      setNotice(t('cards.assign.assigned', { cardId: account.cardId, name: account.fullName }));
      setCardIdDraft('');
      onChanged(account);
    } catch (caught) {
      const fieldMessage = fieldErrorFor(caught, 'cardId');
      if (fieldMessage) {
        setCardFieldError(fieldMessage);
      } else {
        setRootError(caught instanceof ApiError ? caught.message : t('cards.assign.saveFailed'));
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnassign(customer: CustomerAccountDto): Promise<void> {
    setRootError(null);
    setNotice(null);
    setBusyId(customer.id);
    try {
      const account = await unassignCard(customer.id);
      replaceRow(account);
      setNotice(t('cards.assign.unassigned', { name: account.fullName }));
      setRemoving(null);
      onChanged(account);
    } catch (caught) {
      setRemoving(null);
      setRootError(caught instanceof ApiError ? caught.message : t('cards.assign.saveFailed'));
    } finally {
      setBusyId(null);
    }
  }

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';
  const canAssign = cardIdDraft.trim().length > 0;

  return (
    <section
      aria-label={t('cards.assign.title')}
      className="rounded-card border border-subtle bg-raised p-4"
    >
      <h2 className="text-base font-semibold text-content">{t('cards.assign.title')}</h2>

      <label className="mt-3 block">
        <span className="mb-1 block text-sm text-content-secondary">
          {t('cards.assign.cardIdLabel')}
        </span>
        <input
          value={cardIdDraft}
          onChange={(event) => {
            setCardIdDraft(event.target.value);
            setCardFieldError(null);
          }}
          className={`${inputClass} tabular`}
        />
        {cardFieldError ? (
          <span role="alert" className="mt-1 block text-xs text-danger">
            {cardFieldError}
          </span>
        ) : (
          <span className="mt-1 block text-xs text-content-muted">
            {t('cards.assign.cardIdHint')}
          </span>
        )}
      </label>

      <label className="mt-3 block">
        <span className="mb-1 block text-sm text-content-secondary">
          {t('cards.assign.searchLabel')}
        </span>
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={t('cards.assign.searchPlaceholder')}
            className={`${inputClass} ps-8`}
          />
        </div>
      </label>

      {notice ? (
        <p role="status" className="mt-3 text-sm text-available">
          {notice}
        </p>
      ) : null}
      {rootError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {rootError}
        </p>
      ) : null}

      {searchError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {searchError}
        </p>
      ) : loading ? (
        <p className="mt-3 text-sm text-content-muted">{t('common.loading')}</p>
      ) : rows !== null && rows.length === 0 ? (
        <p className="mt-3 text-sm text-content-muted">{t('cards.assign.noMatches')}</p>
      ) : rows !== null ? (
        <ul className="mt-3 divide-y divide-subtle rounded-card border border-subtle">
          {rows.map((customer) => (
            <li key={customer.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-content">{customer.fullName}</p>
                <p className="truncate text-xs text-content-muted">
                  {customer.phone ?? '—'}
                  {' · '}
                  {customer.cardId ? (
                    <span className="tabular">{customer.cardId}</span>
                  ) : (
                    t('cards.assign.noCard')
                  )}
                </p>
              </div>
              {customer.cardId ? (
                <button
                  type="button"
                  onClick={() => setRemoving(customer)}
                  disabled={busyId === customer.id}
                  className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover disabled:opacity-60"
                >
                  <UserRoundX className="h-3.5 w-3.5" aria-hidden />
                  {t('cards.assign.unassign')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void handleAssign(customer)}
                // Disabled with the reason visible, never hidden: a button
                // that quietly does nothing reads as broken software.
                disabled={!canAssign || busyId === customer.id}
                title={canAssign ? undefined : t('cards.assign.assignNeedsCard')}
                className="rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
              >
                {t('cards.assign.assign')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {removing ? (
        <UnassignConfirmDialog
          customer={removing}
          busy={busyId === removing.id}
          onConfirm={() => void handleUnassign(removing)}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * Debounced search against the customers list. Fires only for a non-empty
 * query — mounting this panel must not pull the venue's whole first page.
 */
function useCustomerSearch(search: string) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CustomerAccountDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const trimmed = search.trim();
    if (!trimmed) {
      setRows(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    // Debounced: typing a phone number should not fire a request per digit.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await api.get<CustomerListResponse>(customerSearchPath(trimmed));
          if (!cancelled) {
            setRows(response.rows);
            setError(null);
          }
        } catch (caught) {
          // A failed search renders as a failure — an empty list here would
          // read as "no such customer" and send staff off to re-register one.
          if (!cancelled) {
            setError(caught instanceof ApiError ? caught.message : t('cards.assign.searchFailed'));
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, t]);

  /** Keep the visible list truthful after an assign/unassign round-trip. */
  function replaceRow(account: CustomerAccountDto): void {
    setRows((current) =>
      current === null ? current : current.map((row) => (row.id === account.id ? account : row)),
    );
  }

  return { rows, error, loading, replaceRow };
}

function UnassignConfirmDialog({
  customer,
  busy,
  onConfirm,
  onClose,
}: {
  customer: CustomerAccountDto;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('cards.assign.unassignTitle')}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('cards.assign.unassignTitle')}</h2>
        <p className="mt-2 text-sm text-content-secondary">
          {t('cards.assign.unassignConfirm', {
            cardId: customer.cardId ?? '—',
            name: customer.fullName,
          })}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger-hover disabled:opacity-60"
          >
            {t('cards.assign.confirmRemove')}
          </button>
        </div>
      </div>
    </div>
  );
}
